import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import BeforeAfterSlider from "@/components/shared/BeforeAfterSlider";

/**
 * Rotating before/after gallery.
 * Each pair: { image, room }.
 * Uses a single high-quality "after" photo and simulates the "before" state
 * with a dimmed/desaturated filter (placeholder until real client photos land).
 */
export default function BeforeAfterCarousel({ pairs = [], beforeLabel = "Before", afterLabel = "After" }) {
  const [index, setIndex] = useState(0);
  const count = pairs.length;
  const go = (next) => setIndex(((next % count) + count) % count);
  if (count === 0) return null;
  const pair = pairs[index];

  return (
    <div className="w-full" data-testid="before-after-carousel">
      <div className="relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            data-testid={`before-after-pair-${index}`}
          >
            <BeforeAfterSlider
              before={pair.image}
              after={pair.image}
              dim
              alt={`${pair.room} — before and after Ever Clean Home cleaning`}
              beforeLabel={beforeLabel}
              afterLabel={afterLabel}
            />
          </motion.div>
        </AnimatePresence>

        {/* arrows */}
        <button
          onClick={() => go(index - 1)}
          aria-label="Previous room"
          className="absolute top-1/2 -left-3 sm:-left-5 -translate-y-1/2 w-10 h-10 rounded-full bg-white hover:bg-[#0EA5A4] hover:text-white text-[#0C1A1A] grid place-items-center shadow-lg border border-[#E5E7EB] transition-colors z-10"
          data-testid="before-after-prev"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => go(index + 1)}
          aria-label="Next room"
          className="absolute top-1/2 -right-3 sm:-right-5 -translate-y-1/2 w-10 h-10 rounded-full bg-white hover:bg-[#0EA5A4] hover:text-white text-[#0C1A1A] grid place-items-center shadow-lg border border-[#E5E7EB] transition-colors z-10"
          data-testid="before-after-next"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* room tabs / dots */}
      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="before-after-tabs">
        {pairs.map((p, i) => (
          <button
            key={i}
            onClick={() => setIndex(i)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors ${i === index ? "bg-[#0EA5A4] text-white" : "bg-[#0EA5A4]/10 text-[#0F766E] hover:bg-[#0EA5A4]/20"}`}
            data-testid={`before-after-tab-${i}`}
          >
            {p.room}
          </button>
        ))}
      </div>
    </div>
  );
}
